/*
 * Fitment Labs — Shop-by-Vehicle finder (v3)
 *
 * Replaces the metafield/storefront-filter approach, which could not work:
 * storefront filters must be created in the Shopify admin AND are not displayed
 * on collections over 5,000 products (wheels has 14,552).
 *
 * This drives the cascade from a static generation-level dataset
 * (assets/fitment-data.json) and routes to Shopify TAG-PATH filtering, which
 * needs no app, no admin configuration, and has no collection-size limit:
 *
 *     /collections/wheels/fitment-bolt-pattern-5x114-3+fitment-diameter-18
 *
 * Tag handles convert "." and ":" to "-", so 5x114.3 -> 5x114-3. Getting this
 * wrong returns a 404, which is what made earlier probes look like failures.
 *
 * Registers <fitment-vehicle-finder>, deliberately NOT <fitment-finder>, so the
 * legacy finder in fitment-theme.js is left untouched and cannot double-bind.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'fitment-lab-vehicle';
  var booted = new WeakSet();
  var dataPromise = null;

  function tagValue(value) {
    // Shopify tag handle: "." and ":" both collapse to "-".
    return String(value).replace(/[.:]/g, '-');
  }

  function loadData(url) {
    if (!dataPromise) {
      dataPromise = fetch(url, { credentials: 'same-origin' }).then(function (r) {
        if (!r.ok) throw new Error('Vehicle data could not be loaded.');
        return r.json();
      });
    }
    return dataPromise;
  }

  function Db(raw) {
    this.makes = raw.makes;
    this.models = raw.models;
    this.bps = raw.bps;
    this.gens = raw.gens;
  }

  Db.prototype.modelName = function (makeIdx, modelIdx) {
    return this.models[String(makeIdx)][modelIdx];
  };

  Db.prototype.matching = function (sel) {
    var self = this;
    return this.gens.filter(function (g) {
      if (sel.year && !(g[2] <= sel.year && sel.year <= g[3])) return false;
      if (sel.make && self.makes[g[0]] !== sel.make) return false;
      if (sel.model && self.modelName(g[0], g[1]) !== sel.model) return false;
      if (sel.trim && g[7].indexOf(sel.trim) === -1) return false;
      return true;
    });
  };

  Db.prototype.years = function () {
    var lo = Infinity, hi = -Infinity;
    this.gens.forEach(function (g) {
      if (g[2] < lo) lo = g[2];
      if (g[3] > hi) hi = g[3];
    });
    var out = [];
    for (var y = hi; y >= lo; y -= 1) out.push(String(y));
    return out;
  };

  function uniqSorted(list) {
    var seen = Object.create(null);
    var out = [];
    list.forEach(function (v) {
      if (v && !seen[v]) { seen[v] = 1; out.push(v); }
    });
    out.sort(function (a, b) {
      return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
    });
    return out;
  }

  Db.prototype.optionsFor = function (field, sel) {
    var self = this;
    var gens = this.matching(sel);
    if (field === 'year') return this.years();
    if (field === 'make') return uniqSorted(gens.map(function (g) { return self.makes[g[0]]; }));
    if (field === 'model') return uniqSorted(gens.map(function (g) { return self.modelName(g[0], g[1]); }));
    if (field === 'trim') {
      var trims = [];
      gens.forEach(function (g) { trims = trims.concat(g[7]); });
      return uniqSorted(trims);
    }
    if (field === 'size') {
      var d = [];
      gens.forEach(function (g) { d = d.concat(g[5]); });
      return uniqSorted(d).map(String);
    }
    return [];
  };

  Db.prototype.fitment = function (sel) {
    var self = this;
    var gens = this.matching(sel);
    if (!gens.length) return null;
    return {
      boltPatterns: uniqSorted(gens.map(function (g) { return self.bps[g[4]]; })),
      diameters: uniqSorted(gens.reduce(function (a, g) { return a.concat(g[5]); }, [])),
      bore: gens[0][6]
    };
  };

  function Finder(root) {
    this.root = root;
    this.fields = ['year', 'make', 'model', 'trim', 'size'];
    this.selects = {};
    var self = this;
    this.fields.forEach(function (f) {
      self.selects[f] = root.querySelector('[data-vf="' + f + '"]');
    });
    this.submit = root.querySelector('[data-vf-submit]');
    this.error = root.querySelector('[data-vf-error]');
    this.summary = root.querySelector('[data-vf-summary]');
    this.collection = root.getAttribute('data-collection') || 'wheels';
    this.db = null;

    var src = root.getAttribute('data-src');
    loadData(src).then(function (raw) {
      self.db = new Db(raw);
      self.bind();
      self.restore();
      self.refresh('year');
    }).catch(function (e) {
      self.showError(e.message || 'Vehicle data could not be loaded.');
    });
  }

  Finder.prototype.showError = function (msg) {
    if (!this.error) return;
    this.error.textContent = msg || '';
    this.error.hidden = !msg;
  };

  Finder.prototype.selection = function (upTo) {
    var sel = {};
    var stop = upTo === undefined ? this.fields.length : upTo;
    for (var i = 0; i < stop; i += 1) {
      var f = this.fields[i];
      var el = this.selects[f];
      if (!el || !el.value) break;
      sel[f] = f === 'year' ? parseInt(el.value, 10) : el.value;
    }
    return sel;
  };

  Finder.prototype.bind = function () {
    var self = this;
    this.fields.forEach(function (f, i) {
      var el = self.selects[f];
      if (!el) return;
      el.addEventListener('change', function () {
        self.showError('');
        self.refresh(self.fields[i + 1]);
      });
    });
    if (this.submit) {
      this.submit.addEventListener('click', function () { self.go(); });
    }
    var reset = this.root.querySelector('[data-vf-reset]');
    if (reset) {
      reset.addEventListener('click', function () {
        self.fields.forEach(function (f) {
          var el = self.selects[f];
          if (el) el.value = '';
        });
        try { window.localStorage.removeItem(STORAGE_KEY); } catch (e) {}
        self.showError('');
        self.refresh('year');
      });
    }
  };

  // Repopulate `field` and everything downstream of it.
  Finder.prototype.refresh = function (field) {
    if (!this.db) return;
    var start = field ? this.fields.indexOf(field) : 0;
    if (start < 0) start = 0;

    for (var i = start; i < this.fields.length; i += 1) {
      var f = this.fields[i];
      var el = this.selects[f];
      if (!el) continue;

      var sel = this.selection(i);
      var ready = i === 0 || Object.keys(sel).length === i;
      var opts = ready ? this.db.optionsFor(f, sel) : [];
      var previous = el.value;

      var placeholder = el.getAttribute('data-placeholder') || f.toUpperCase();
      el.innerHTML = '';
      el.add(new Option(placeholder, ''));
      opts.forEach(function (o) {
        el.add(new Option(f === 'size' ? o + '"' : o, o));
      });

      // Keep the prior choice when it survives the new, narrower option set —
      // changing Trim shouldn't silently discard a still-valid Size. Anything
      // that did NOT survive must be cleared explicitly: leaving it set would
      // let a stale value (e.g. Altima under Ford) reach the URL builder.
      if (previous && opts.indexOf(previous) !== -1) el.value = previous;
      else el.value = '';

      el.disabled = opts.length === 0;
    }
    this.update();
  };

  Finder.prototype.required = function () {
    // Size is an optional refinement; year/make/model/trim identify the vehicle.
    return ['year', 'make', 'model', 'trim'];
  };

  Finder.prototype.complete = function () {
    var self = this;
    return this.required().every(function (f) {
      var el = self.selects[f];
      return el && el.value;
    });
  };

  Finder.prototype.update = function () {
    var ok = this.complete();
    if (this.submit) this.submit.disabled = !ok;
    if (!this.summary) return;

    if (!ok) { this.summary.hidden = true; return; }
    var sel = this.selection();
    var fit = this.db.fitment({ year: sel.year, make: sel.make, model: sel.model, trim: sel.trim });
    if (!fit) { this.summary.hidden = true; return; }

    this.summary.hidden = false;
    this.summary.innerHTML =
      '<strong>' + [sel.year, sel.make, sel.model, sel.trim].join(' ') + '</strong>' +
      '<span>BOLT PATTERN <b>' + fit.boltPatterns.join(' / ') + '</b></span>' +
      '<span>OE SIZES <b>' + fit.diameters.join('&#34; / ') + '&#34;</b></span>' +
      (fit.bore ? '<span>HUB BORE <b>' + fit.bore + 'mm</b></span>' : '');
  };

  Finder.prototype.destination = function () {
    var sel = this.selection();
    var fit = this.db.fitment({ year: sel.year, make: sel.make, model: sel.model, trim: sel.trim });
    if (!fit || !fit.boltPatterns.length) return null;

    // Shopify tag paths OR within a facet and AND across facets, so a single
    // bolt pattern is required for a correct narrow. Generations here resolve
    // to exactly one; if that ever changes, prefer the first deterministically.
    var parts = ['fitment-bolt-pattern-' + tagValue(fit.boltPatterns[0])];
    var size = this.selects.size && this.selects.size.value;
    if (size) parts.push('fitment-diameter-' + tagValue(size));

    return '/collections/' + this.collection + '/' + parts.join('+');
  };

  Finder.prototype.go = function () {
    if (!this.complete()) {
      this.showError('Choose your vehicle to see what fits.');
      return;
    }
    var url = this.destination();
    if (!url) {
      this.showError('We do not have fitment data for that vehicle yet. Call us and we will match it by hand.');
      return;
    }
    var sel = this.selection();
    var fit = this.db.fitment({ year: sel.year, make: sel.make, model: sel.model, trim: sel.trim });
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        version: 3,
        values: sel,
        label: [sel.year, sel.make, sel.model, sel.trim].filter(Boolean).join(' '),
        boltPattern: fit ? fit.boltPatterns[0] : null,
        url: url,
        savedAt: new Date().toISOString()
      }));
    } catch (e) {}
    window.location.assign(url);
  };

  Finder.prototype.restore = function () {
    var saved;
    try { saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null'); } catch (e) { return; }
    if (!saved || !saved.values) return;
    var self = this;
    // Apply saved values in cascade order so each step's options exist first.
    this.fields.forEach(function (f, i) {
      self.refresh(f);
      var el = self.selects[f];
      var want = saved.values[f];
      if (el && want !== undefined && want !== null) {
        var str = String(want);
        for (var o = 0; o < el.options.length; o += 1) {
          if (el.options[o].value === str) { el.value = str; break; }
        }
      }
    });
    this.update();
  };

  function boot(scope) {
    var ctx = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(ctx.querySelectorAll('fitment-vehicle-finder'), function (el) {
      if (booted.has(el)) return;
      booted.add(el);
      new Finder(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }
  // Theme editor re-renders sections without a full page load.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
})();
